import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  Users, DollarSign, TrendingUp, Award, Copy, CheckCircle,
  Clock, ArrowRight, LogOut, RefreshCw, ExternalLink, ChevronRight
} from 'lucide-react';
import { getFrontendUrl } from '../utils/urlConfig';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReferralDashboardPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [codeCopied, setCodeCopied] = useState(false);

  const fetchDashboard = useCallback(async () => {
    const token = localStorage.getItem('referral_token');
    if (!token) {
      navigate('/referral/login');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/referral-partner/dashboard`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (res.status === 401 || res.status === 403) {
        localStorage.removeItem('referral_token');
        localStorage.removeItem('referral_partner');
        navigate('/referral/login');
        return;
      }

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to fetch dashboard');
      
      setDashboard(data);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const handleLogout = () => {
    localStorage.removeItem('referral_token');
    localStorage.removeItem('referral_partner');
    toast.success('Logged out successfully');
    navigate('/referral/login');
  };

  const copyReferralCode = () => {
    if (dashboard?.partner?.referral_code) {
      navigator.clipboard.writeText(dashboard.partner.referral_code);
      setCodeCopied(true);
      toast.success('Referral code copied!');
      setTimeout(() => setCodeCopied(false), 2000);
    }
  };

  const copyReferralLink = () => {
    if (dashboard?.partner?.referral_code) {
      const link = `${getFrontendUrl()}/register?ref=${dashboard.partner.referral_code}`;
      navigator.clipboard.writeText(link);
      toast.success('Referral link copied!');
    }
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'gold': return 'from-yellow-400 to-yellow-600 text-yellow-900';
      case 'silver': return 'from-gray-300 to-gray-500 text-gray-900';
      default: return 'from-amber-500 to-amber-700 text-amber-900';
    }
  };

  const getTierBgColor = (tier) => {
    switch (tier) {
      case 'gold': return 'bg-yellow-100 border-yellow-300';
      case 'silver': return 'bg-gray-100 border-gray-300';
      default: return 'bg-amber-100 border-amber-300';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-emerald-500/20 text-emerald-400';
      case 'pending': return 'bg-yellow-500/20 text-yellow-400';
      case 'paid': return 'bg-blue-500/20 text-blue-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="animate-spin text-emerald-400 mx-auto mb-4" size={40} />
          <p className="text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-400 mb-4">Failed to load dashboard</p>
          <button onClick={fetchDashboard} className="px-4 py-2 bg-emerald-500 text-white rounded-lg">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const { partner, stats, recent_registrations } = dashboard;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
      {/* Header */}
      <header className="bg-black/30 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/referral" className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">
              EvalentX
            </Link>
            <span className="text-gray-500">|</span>
            <span className="text-gray-400">Referral Partner</span>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={fetchDashboard}
              className="p-2 text-gray-400 hover:text-white transition-colors"
              title="Refresh"
            >
              <RefreshCw size={20} />
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
              data-testid="logout-btn"
            >
              <LogOut size={18} />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Welcome Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold mb-1">Welcome, {partner.full_name}!</h1>
            <p className="text-gray-400">{partner.email}</p>
          </div>
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${getTierBgColor(partner.tier)} border`}>
            <Award className={`${partner.tier === 'gold' ? 'text-yellow-600' : partner.tier === 'silver' ? 'text-gray-600' : 'text-amber-600'}`} size={20} />
            <span className={`font-bold capitalize ${partner.tier === 'gold' ? 'text-yellow-700' : partner.tier === 'silver' ? 'text-gray-700' : 'text-amber-700'}`}>
              {partner.tier} Tier
            </span>
          </div>
        </div>

        {/* Referral Code Card */}
        <div className="bg-gradient-to-r from-emerald-900/50 to-cyan-900/50 rounded-2xl p-6 border border-emerald-500/30 mb-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <p className="text-gray-400 text-sm mb-2">Your Referral Code</p>
              <div className="flex items-center gap-3">
                <span className="text-3xl font-mono font-bold text-white" data-testid="referral-code">
                  {partner.referral_code}
                </span>
                <button
                  onClick={copyReferralCode}
                  className="p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                  title="Copy code"
                >
                  {codeCopied ? <CheckCircle className="text-emerald-400" size={20} /> : <Copy size={20} />}
                </button>
              </div>
            </div>
            <button
              onClick={copyReferralLink}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors"
            >
              <ExternalLink size={18} />
              Copy Referral Link
            </button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <span className="text-gray-400 text-sm">Total Referrals</span>
              <Users className="text-blue-400" size={20} />
            </div>
            <p className="text-3xl font-bold" data-testid="total-referrals">{stats.total_referrals}</p>
            <p className="text-sm text-gray-500 mt-1">
              {stats.pending_referrals} pending
            </p>
          </div>

          <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <span className="text-gray-400 text-sm">Successful</span>
              <CheckCircle className="text-emerald-400" size={20} />
            </div>
            <p className="text-3xl font-bold text-emerald-400" data-testid="successful-referrals">
              {stats.successful_referrals}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              Paid registrations
            </p>
          </div>

          <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <span className="text-gray-400 text-sm">Total Earnings</span>
              <DollarSign className="text-yellow-400" size={20} />
            </div>
            <p className="text-3xl font-bold" data-testid="total-earnings">₹{stats.total_earnings.toLocaleString()}</p>
            <p className="text-sm text-emerald-400 mt-1">
              ₹{stats.pending_earnings.toLocaleString()} pending
            </p>
          </div>

          <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <span className="text-gray-400 text-sm">Commission Rate</span>
              <TrendingUp className="text-purple-400" size={20} />
            </div>
            <p className="text-3xl font-bold">₹{stats.current_commission_rate}</p>
            <p className="text-sm text-gray-500 mt-1">
              Per referral
            </p>
          </div>
        </div>

        {/* Tier Progress */}
        {stats.next_tier && (
          <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Progress to {stats.next_tier.charAt(0).toUpperCase() + stats.next_tier.slice(1)} Tier</h3>
              <span className="text-sm text-gray-400">
                {stats.referrals_to_next_tier} more referrals needed
              </span>
            </div>
            <div className="h-3 bg-gray-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full transition-all duration-500"
                style={{ 
                  width: `${Math.min(100, ((stats.next_tier === 'silver' ? stats.successful_referrals : stats.successful_referrals - 10) / (stats.next_tier === 'silver' ? 10 : 10)) * 100)}%` 
                }}
              ></div>
            </div>
            <p className="text-sm text-gray-400 mt-2">
              Upgrade to earn ₹{stats.next_tier === 'silver' ? '200' : '250'} per referral
            </p>
          </div>
        )}

        {/* Recent Registrations */}
        <div className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden">
          <div className="flex items-center justify-between p-6 border-b border-gray-700">
            <h3 className="font-semibold">Recent Registrations</h3>
            <Link 
              to="/referral/registrations" 
              className="text-sm text-emerald-400 hover:text-emerald-300 flex items-center gap-1"
            >
              View all <ChevronRight size={16} />
            </Link>
          </div>
          
          {recent_registrations.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="mx-auto text-gray-600 mb-4" size={48} />
              <p className="text-gray-400 mb-2">No registrations yet</p>
              <p className="text-sm text-gray-500">Share your referral code to start earning!</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-700">
              {recent_registrations.map((reg) => (
                <div key={reg.id} className="p-4 hover:bg-gray-700/30 transition-colors">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{reg.team_name}</p>
                      <p className="text-sm text-gray-400">{reg.leader_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-emerald-400">+₹{reg.commission_amount}</p>
                      <span className={`inline-block px-2 py-0.5 rounded text-xs ${getStatusColor(reg.status)}`}>
                        {reg.status}
                      </span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {new Date(reg.registered_at).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* How to Use Section */}
        <div className="mt-8 bg-gray-800/30 rounded-xl p-6 border border-gray-700">
          <h3 className="font-semibold mb-4">How to Use Your Referral Code</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                <span className="text-emerald-400 font-bold">1</span>
              </div>
              <div>
                <p className="font-medium">Share Your Code</p>
                <p className="text-sm text-gray-400">Share your unique code with friends and on social media</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                <span className="text-emerald-400 font-bold">2</span>
              </div>
              <div>
                <p className="font-medium">They Register</p>
                <p className="text-sm text-gray-400">They enter your code during registration</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                <span className="text-emerald-400 font-bold">3</span>
              </div>
              <div>
                <p className="font-medium">You Earn</p>
                <p className="text-sm text-gray-400">Get commission when they complete payment</p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ReferralDashboardPage;
